import React, { useState, useMemo } from "react";
import DatePicker from "../../../../components/inputs/MomentDatePicker";
import { message, Spin, Typography, Divider, Table, Tag, Switch } from "antd";
import moment from "moment";
import { useSelector } from "react-redux";
import { selectUser } from "../../../../redux/slices/auth.slice";
import { useGetInstructorReport } from "../../../../services/report.service";

const { RangePicker } = DatePicker;
const { Text } = Typography;

const Report = () => {
  const user = useSelector(selectUser);
  const [isLiveView, setIsLiveView] = useState(false);
  const [dateRange, setDateRange] = useState([
    moment().startOf("month"),
    moment(),
  ]);

  const queryParams = {
    instructor_id: undefined,
    start_date: dateRange[0]?.format("YYYY-MM-DD"),
    end_date: dateRange[1]?.format("YYYY-MM-DD"),
  };

  const {
    data: reportData,
    isLoading,
    error,
    isFetching,
  } = useGetInstructorReport(queryParams, {
    enabled: !!user && !!(dateRange && dateRange[0] && dateRange[1]),
    refetchInterval: isLiveView ? 60000 : false,
    refetchOnWindowFocus: isLiveView,
  });

  const onDateRangeChange = (dates) => {
    if (!dates) {
      setDateRange([]);
      return;
    }
    const diff = dates[1].diff(dates[0], "days");
    if (diff > 30) {
      message.error("Date range cannot exceed 31 days");
      return;
    }
    setDateRange(dates);
  };

  const allDates = useMemo(() => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) return [];

    const dates = [];
    let current = moment(dateRange[0]);
    while (current.isSameOrBefore(dateRange[1])) {
      dates.push({
        dateStr: current.format("YYYY-MM-DD"),
        displayDate: current.format("D/M"),
        dayName: current.format("ddd"),
        moment: current.clone(),
      });
      current.add(1, "day");
    }
    return dates;
  }, [dateRange]);

  const getStatusBadge = (status) => {
    switch (status) {
      case "leave":
        return <Tag color="orange">L</Tag>;
      case "sick":
        return <Tag color="red">S</Tag>;
      case "half_day":
        return <Tag color="blue">H</Tag>;
      case "permission":
        return <Tag color="purple">P</Tag>;
      case "check_in":
        return <Tag color="cyan">L</Tag>;
      case "check_out":
        return <Tag color="cyan">E</Tag>;
      case "on_time":
        return <Tag color="green">P</Tag>;
      case "short_time":
        return <Tag color="gold">T</Tag>;
      case "missed":
        return <Tag color="red">M</Tag>;
      case "holiday":
        return <Tag color="pink">H</Tag>;
      default:
        return <Tag color="default">-</Tag>;
    }
  };

  const columns = useMemo(() => {
    if (!reportData?.data?.instructors || allDates.length === 0) return [];

    const dateColumns = allDates.map((date) => ({
      title: (
        <div className="text-center">
          <div className="font-bold text-gray-800 text-xs">
            {date.displayDate}
          </div>
          <div className="text-[10px] text-blue-500 lowercase">
            {date.dayName}
          </div>
        </div>
      ),
      key: date.dateStr,
      width: 100,
      render: (_, record) => {
        const instructor = record;
        const dateRecord = instructor.records?.find(
          (r) => r.date === date.dateStr,
        );

        let displayStatus = dateRecord?.status;
        const eh = dateRecord?.excused_hours;
        const isAbsentOrUnmarked =
          !displayStatus ||
          ["unmarked", "missed", "absent"].includes(displayStatus);

        if (eh && isAbsentOrUnmarked) {
          if (eh.leave_days > 0) displayStatus = "leave";
          else if (eh.sick_days > 0) displayStatus = "sick";
          else if (
            eh.half_day_hours &&
            eh.half_day_hours !== "0:00" &&
            eh.half_day_hours !== 0
          )
            displayStatus = "half_day";
          else if (
            eh.permission_hours &&
            eh.permission_hours !== "0:00" &&
            eh.permission_hours !== 0
          )
            displayStatus = "permission";
        }

        return (
          <div className="flex flex-col items-center px-1">
            <div
              className={`font-bold text-sm ${
                dateRecord?.status === "missed"
                  ? "text-red-500"
                  : "text-gray-800"
              }`}
            >
              {dateRecord?.ah_display || "0:00"}
            </div>
            <div className="w-full border-t border-black my-1"></div>
            <div
              className={`text-xs font-medium ${
                dateRecord?.eh_display && dateRecord.eh_display !== "0:00"
                  ? "text-blue-600"
                  : "text-gray-400"
              }`}
            >
              {dateRecord?.eh_display || "0:00"}
            </div>
            {displayStatus && displayStatus !== "missed" && (
              <div className="mt-1">{getStatusBadge(displayStatus)}</div>
            )}
          </div>
        );
      },
    }));

    return [
      {
        title: (
          <div className="flex flex-col items-center py-1">
            <div className="font-black text-gray-800 uppercase text-[11px] tracking-wider">
              Instructor
            </div>
          </div>
        ),
        dataIndex: "instructor_name",
        key: "instructor_name",
        fixed: "left",
        width: 180,
        render: (text) => (
          <div className="flex items-center justify-between gap-2 w-full px-2">
            <div className="font-bold text-gray-800 truncate">{text}</div>
            <div className="flex flex-col items-center shrink-0 bg-gray-50 rounded px-1.5 py-0.5 border border-gray-200 min-w-8">
              <div className="text-[9px] text-blue-500 font-black uppercase tracking-tighter">
                AH
              </div>
              <div className="w-full border-t border-black my-0.5"></div>
              <div className="text-[9px] text-blue-500 font-black uppercase tracking-tighter">
                EH
              </div>
            </div>
          </div>
        ),
      },
      ...dateColumns,
      {
        title: (
          <div className="flex flex-col items-center py-1">
            <div className="font-black text-gray-800 uppercase text-[11px] tracking-wider">
              Total Hours
            </div>
          </div>
        ),
        key: "total_hours",
        fixed: "right",
        width: 120,
        render: (_, record) => (
          <div className="flex flex-col items-center px-1">
            <div className="font-bold text-lg text-orange-600">
              {record.total_actual_hours || "0:00"}
            </div>
            <div className="w-full border-t border-black my-1"></div>
            <div className="text-sm font-bold text-blue-500">
              {record.total_eh_hours || "0:00"}
            </div>
          </div>
        ),
      },
    ];
  }, [reportData, allDates]);

  const tableData = useMemo(() => {
    if (!reportData?.data?.instructors) return [];

    return reportData.data.instructors.map((instructor) => {
      let totalEhMinutes = 0;
      instructor.records?.forEach((rec) => {
        const eh = rec.eh_display || "0:00";
        if (eh !== "0:00") {
          const [h, m] = eh.split(":").map(Number);
          totalEhMinutes += h * 60 + m;
        }
      });
      const h = Math.floor(totalEhMinutes / 60);
      const m = totalEhMinutes % 60;
      const total_eh_hours = `${h}:${m.toString().padStart(2, "0")}`;

      return {
        key: instructor.instructor_id,
        ...instructor,
        total_eh_hours,
      };
    });
  }, [reportData]);

  return (
    <div className="mt-8 min-h-screen">
      <div className="bg-white drop-shadow-md p-8 rounded-lg w-full">
        <div className="text-4xl text-orange-500 font-bold py-4 mb-4 border-b-2 border-gray-200">
          Personal Attendance Report
        </div>

        <div className="mb-8 flex items-center justify-between bg-linear-to-r from-gray-50 to-white p-6 rounded-xl border-2 border-gray-200 shadow-sm">
          <div className="flex flex-col gap-1">
            <Text strong className="text-gray-700 font-semibold text-lg">
              Selected Date Range
            </Text>
            <RangePicker
              value={dateRange.length ? dateRange : null}
              onChange={onDateRangeChange}
              format="DD/MM/YYYY"
              style={{ width: "320px", height: "40px" }}
              className="rounded-lg shadow-sm"
            />
          </div>

          <div className="flex items-center gap-3 bg-white px-4 py-3 rounded-lg border border-gray-200 shadow-sm">
            <span className="text-sm font-medium text-gray-700">
              <span className="mr-1">🔴</span> Live View:
            </span>
            <Switch
              size="small"
              checked={isLiveView}
              onChange={setIsLiveView}
              className={isLiveView ? "bg-green-500" : ""}
            />
            {isFetching && isLiveView && (
              <div className="flex items-center ml-3 pl-3 border-l border-gray-200">
                <Spin size="small" className="mr-2" />
                <span className="text-xs text-blue-600 font-bold">
                  Updating...
                </span>
              </div>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-24 gap-4">
            <Spin size="large" />
            <Text type="secondary" className="animate-pulse">
              Loading timesheet...
            </Text>
          </div>
        ) : error ? (
          <div className="text-center text-red-500 py-10 bg-red-50 rounded-lg border border-red-200">
            Error fetching your report: {error.message}
          </div>
        ) : allDates.length > 0 ? (
          <div className="space-y-4">
            <div className="overflow-x-auto">
              <Table
                columns={columns}
                dataSource={tableData}
                loading={isLoading}
                scroll={{ x: "max-content", y: 600 }}
                pagination={false}
                bordered
                size="middle"
                className="custom-report-table"
                rowClassName="hover:bg-blue-50 transition-colors duration-200"
                style={{
                  borderColor: "#000000",
                }}
              />
            </div>

            <Divider className="border-black" />
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-1">
                <Tag color="orange">L</Tag>
                <span className="text-xs">= Leave</span>
              </div>
              <div className="flex items-center gap-1">
                <Tag color="red">S</Tag>
                <span className="text-xs">= Sick</span>
              </div>
              <div className="flex items-center gap-1">
                <Tag color="blue">H</Tag>
                <span className="text-xs">= Half Day</span>
              </div>
              <div className="flex items-center gap-1">
                <Tag color="green">P</Tag>
                <span className="text-xs">= Present</span>
              </div>
              <div className="flex items-center gap-1">
                <Tag color="purple">P</Tag>
                <span className="text-xs">= Permission</span>
              </div>
              <div className="flex items-center gap-1">
                <Tag color="pink">H</Tag>
                <span className="text-xs">= Holiday</span>
              </div>
              <div className="flex items-center gap-1">
                <Tag color="blue">AH</Tag>
                <span className="text-xs">= Actual Hours</span>
              </div>
              <div className="flex items-center gap-1">
                <Tag color="blue">EH</Tag>
                <span className="text-xs">= Exception Hours</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="py-24 flex justify-center items-center border border-dashed border-gray-300 rounded-lg bg-gray-50">
            <Text type="secondary">
              No records found for the selected month.
            </Text>
          </div>
        )}
      </div>
    </div>
  );
};

export default Report;
